/* File: src/components/game/GamePlayingScreen.js */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import GameHeader from './GameHeader';
import PositionGrid from './PositionGrid';
import AudioLetterDisplay from './AudioLetterDisplay';
import GameControls from './GameControls';
// *** REMOVED: GamePaused import is no longer needed here ***
// *** REMOVED: useContext and useGame imports (if previously used solely for pauseReason) ***

/**
 * Renders the main game interface when the game state is 'playing' or 'paused'.
 * Displays the header (progress, pause/resume), the visual grid, optional audio letter display,
 * and game controls. Dimming/disabling of content during pause or other overlays
 * (like navigation confirmation) is controlled by the `isOverlayVisible` prop passed down
 * from the parent GameScreen component.
 */
const GamePlayingScreen = ({
  // --- Props passed down from GameScreen ---

  // Core Game State
  nBack,
  difficulty,
  sequence,
  currentTrial,
  totalTrials,
  gameState, // Still needed for header, controls, grid state etc.

  // Feedback State (from useGameResponses)
  positionFeedback,
  audioFeedback,
  missedMatchTrial,
  visualResponseTrials,
  audioResponseTrials,

  // Response Handlers (from useGameResponses)
  handlePositionResponse,
  handleAudioResponse,

  // Control Handlers (from GameScreen/useGameControls)
  handlePauseResume, // Used by GameHeader for the pause/resume button

  // *** REMOVED: resumeGame, handleEndGame props are no longer needed ***
  // These actions are now triggered by the GamePaused component rendered in GameScreen

  // Configuration & Utility Props
  renderControls = true,
  logReplayEvent,
  getStimulusDuration,
  instantFeedbackIsEnabled,

  // *** ADDED: isOverlayVisible prop ***
  // Flag indicating if an overlay (like GamePaused or ConfirmationModal)
  // is currently active above this component in the parent (GameScreen).
  isOverlayVisible

}) => {

  // --- State for Novice Help Features ---
  // State to toggle the visibility of helper UI elements for low difficulty levels
  const [showNoviceHelp, setShowNoviceHelp] = useState(false);

  // Effect to reset novice help visibility when difficulty changes (e.g., new game)
  useEffect(() => {
    setShowNoviceHelp(false);
  }, [difficulty]);

  // Determine if the current level qualifies for showing novice helpers
  const isNoviceLevel = difficulty <= 0.20; // Threshold for showing helper toggle

  // Determine if the AudioLetterDisplay component should be rendered
  // Only shown if it's a novice level AND the user has explicitly toggled helpers ON
  const showAudioDisplayComponent = isNoviceLevel && showNoviceHelp;

  // --- Calculate Stimulus Duration ---
  // Memoized calculation to avoid re-running on every render unless the getter changes
  const stimulusDisplayDuration = useMemo(() => {
    if (typeof getStimulusDuration === 'function') {
      const duration = getStimulusDuration();
      // Use the calculated duration or a fallback if the getter returns null/undefined/invalid
      return duration && duration > 0 ? duration : 1000; // Default to 1000ms (1 second)
    }
    // Fallback if the getter function wasn't provided
    return 1000;
  }, [getStimulusDuration]); // Dependency: the getter function reference

  // --- Callback for Novice Help Toggle ---
  // Memoized handler for the toggle button in the header
  const toggleNoviceHelp = useCallback(() => {
    setShowNoviceHelp(prev => !prev);
  }, []); // No dependencies needed as it only toggles internal state

  // --- Determine if content should be dimmed/disabled ---
  // Use the isOverlayVisible prop passed down directly from GameScreen
  const dimContent = isOverlayVisible;

  // --- Render Logic ---
  return (
    // Main screen container: Full height, centered content, padding, allows scrolling if needed
    <div className="h-full bg-gray-100 flex items-center justify-center pt-8 px-4 pb-6 sm:pt-12 sm:px-6 sm:pb-8 overflow-y-auto">

      {/* Game Card: White background, rounded corners, shadow, responsive max width */}
      {/* Kept 'relative' in case any *internal* elements rely on it, but the pause overlay is now OUTSIDE this element */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-xl w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl border border-gray-200 my-4 relative">

        {/* === Game Header === */}
        {/* Displays progress, score (optional), pause/resume button, helper toggle */}
        <GameHeader
          nBack={nBack}
          difficulty={difficulty}
          currentTrial={currentTrial}
          totalTrials={totalTrials}
          gameState={gameState}
          onPauseResume={handlePauseResume} // Still needed for the header button
          isNoviceLevel={isNoviceLevel}
          showNoviceHelp={showNoviceHelp}
          onToggleNoviceHelp={toggleNoviceHelp}
        />

        {/* *** REMOVED: Rendering of GamePaused component is now handled in GameScreen *** */}

        {/* === Central Content Area (Grid & Audio Display) === */}
        {/* Flex container to arrange visual grid and optional audio display */}
        {/* Apply subtle opacity reduction and disable pointer events if an overlay is visible */}
        {/* Uses the 'dimContent' flag derived from the 'isOverlayVisible' prop */}
        <div className={`flex flex-col sm:flex-row justify-center items-center mt-6 mb-6 min-h-[300px] ${dimContent ? 'opacity-50 pointer-events-none' : ''} transition-opacity duration-300`}>

          {/* Position Grid Container */}
          {/* Takes up available space, potentially shrinking if audio display is present */}
          <div className={`flex justify-center items-center w-full sm:flex-1 ${showAudioDisplayComponent ? 'sm:mr-4' : ''}`}>
            <PositionGrid
              // Pass necessary props for displaying stimuli and feedback
              currentTrial={currentTrial}
              sequence={sequence}
              nBack={nBack}
              difficulty={difficulty}
              visualFeedback={positionFeedback}
              missedMatchTrial={missedMatchTrial}
              instantFeedbackIsEnabled={instantFeedbackIsEnabled}
              visualResponseTrials={visualResponseTrials}
              logReplayEvent={logReplayEvent}
              stimulusDisplayDuration={stimulusDisplayDuration} // Pass calculated duration
              showNoviceHelp={showNoviceHelp} // Pass flag for internal grid logic
              isReplayMode={false} // Assume not replay mode here
              isTutorialMode={false} // Assume not tutorial mode here
              gameState={gameState} // Pass gameState if needed by grid logic (e.g., animations)
            />
          </div>

          {/* Audio Letter Display Container (Conditional) */}
          {/* Only rendered for novice levels when help is toggled on */}
          {showAudioDisplayComponent && (
            <div className="w-full sm:w-auto mt-4 sm:mt-0 sm:flex-shrink-0 flex justify-center">
              <AudioLetterDisplay
                // Pass necessary props for displaying audio info and feedback
                sequence={sequence}
                currentTrial={currentTrial}
                nBack={nBack}
                difficulty={difficulty}
                audioFeedback={audioFeedback}
                showNoviceHelp={showNoviceHelp} // Pass flag for internal display logic
                isReplayMode={false}
                isTutorialMode={false}
              />
            </div>
          )}
        </div> {/* End Central Content Area */}

        {/* === Game Controls Area (Match Buttons) === */}
        {/* Also apply subtle opacity reduction and disable pointer events if an overlay is visible */}
        {/* Uses the 'dimContent' flag derived from the 'isOverlayVisible' prop */}
        <div className={`mt-2 ${dimContent ? 'opacity-50 pointer-events-none' : ''} transition-opacity duration-300`}>
          {/* Conditionally render the match buttons */}
          {renderControls && (
            <GameControls
              onPositionResponse={handlePositionResponse} // Handler for visual match button
              onAudioResponse={handleAudioResponse}       // Handler for audio match button
              positionFeedback={positionFeedback} // Pass feedback state for button styling
              audioFeedback={audioFeedback}       // Pass feedback state for button styling
              gameState={gameState}               // Pass gameState if controls need it
              missedMatchTrial={missedMatchTrial} // Pass missed match index if needed
              sequence={sequence}                 // Pass sequence if needed by controls
              currentTrial={currentTrial} // Pass current trial index if needed
            />
          )}
        </div>
      </div> {/* End Game Card */}
    </div> // End Main Screen Container
  );
};

export default GamePlayingScreen;